import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import { BusinessUnitSettingsService } from './business-unit-settings.service';
import { firstValueFrom } from 'rxjs';

export interface IBusinessUnitBranding {
    url: string;
    css: string;
    bu: string;
    theme: string;
    icon: string;
    logo: string;
    website: string;
    termsAndCondition: string;
    supportEmail: string;
}

@Injectable()
export class BrandingService {

    private readonly HS = 'hs';
    private readonly DH = 'dh';
    private readonly WHS = 'whs';
    private readonly HORIZON = 'horizon';

    private currentBusinessUnit: IBusinessUnitBranding;

    constructor(private translateService: TranslateService,
        private businessUnitSettingsService: BusinessUnitSettingsService
    ) {
        this.currentBusinessUnit = this.getCurrentBusinessUnit();
        this.initializeTermsAndConditions();
    }

    private async initializeTermsAndConditions(): Promise<void> {
        if (this.currentBusinessUnit) {
            try {
                const link = await firstValueFrom(this.businessUnitSettingsService.getTermsAndConditionsLink());
                if (link) {
                    this.currentBusinessUnit.termsAndCondition = link;
                }
            } catch (error) {
                console.warn('Failed to load terms and conditions link:', error);
            }
        }
    }

    currentlyOn(bu: string): boolean {
        for (const el of environment.businessunits) {
            if (el.bu === bu && window.location.host === el.url) {
                return true;
            }
        }
        return false;
    }

    getLogoPath(): string {
        return this.currentBusinessUnit.logo;
    }

    getFavIcon(): string {
        return this.currentBusinessUnit.icon;
    }

    getBackgroundImage(): string {
        return this.currentBusinessUnit.logo;
    }

    getCurrentBusinessUnit(): IBusinessUnitBranding {
        for (const el of environment.businessunits) {
            if (window.location.host === el.url) {
                return el;
            }
        }
        return undefined;
    }

    getFooterDetails(): string {
        let footer: string;
        switch (this.currentBusinessUnit.bu) {
            case this.HS:
                this.translateService.get('SHARED.HS-FOOTER').subscribe(t => footer = t);
                return footer;
            case this.DH:
                this.translateService.get('SHARED.DH-FOOTER').subscribe(t => footer = t);
                return footer;
            case this.WHS:
                this.translateService.get('SHARED.WHS-FOOTER').subscribe(t => footer = t);
                return footer;
            case this.HORIZON:
                this.translateService.get('SHARED.HORIZON-FOOTER').subscribe(t => footer = t);
                return footer;
            default:
                footer = undefined;
                return footer;
        }
    }

    getLoginBody(): string {
        let description: string;
        switch (this.currentBusinessUnit.bu) {
            case this.HS:
                this.translateService.get('LOGIN.HS-DESCRIPTIONBODY').subscribe(t => description = t);
                return description;
            case this.DH:
                this.translateService.get('LOGIN.DH-DESCRIPTIONBODY').subscribe(t => description = t);
                return description;
            case this.WHS:
                this.translateService.get('LOGIN.WHS-DESCRIPTIONBODY').subscribe(t => description = t);
                return description;
            case this.HORIZON:
                this.translateService.get('LOGIN.HORIZON-DESCRIPTIONBODY').subscribe(t => description = t);
                return description;
            default:
                description = undefined;
                return description;
        }
    }

    getLoginFooter(): string {
        let footerDetails: string;
        switch (this.currentBusinessUnit.bu) {
            case this.HS:
                this.translateService.get('LOGIN.HS-DESCRIPTIONFOOTER').subscribe(t => footerDetails = t);
                return footerDetails;
            case this.DH:
                this.translateService.get('LOGIN.DH-DESCRIPTIONFOOTER').subscribe(t => footerDetails = t);
                return footerDetails;
            case this.WHS:
                this.translateService.get('LOGIN.WHS-DESCRIPTIONFOOTER').subscribe(t => footerDetails = t);
                return footerDetails;
            case this.HORIZON:
                this.translateService.get('LOGIN.HORIZON-DESCRIPTIONFOOTER').subscribe(t => footerDetails = t);
                return footerDetails;
            default:
                footerDetails = undefined;
                return footerDetails;
        }
    }

    getWebsite(): string {
        return this.currentBusinessUnit.website;
    }

    getLoginTitle(): string {
        let titleDetails: string;
        switch (this.currentBusinessUnit.bu) {
            case this.WHS:
                this.translateService.get('LOGIN.WHS-DESCRIPTIONTITLE').subscribe(t => titleDetails = t);
                return titleDetails;
            case this.DH:
                this.translateService.get('LOGIN.DH-DESCRIPTIONTITLE').subscribe(t => titleDetails = t);
                return titleDetails;
            case this.HS:
                this.translateService.get('LOGIN.HS-DESCRIPTIONTITLE').subscribe(t => titleDetails = t);
                return titleDetails;
            default:
                titleDetails = undefined;
                return titleDetails;
        }
    }

    getTermsAndConditionsLink(): string {
        return this.currentBusinessUnit.termsAndCondition;
    }

    getSupportEmail(): string {
        return this.currentBusinessUnit.supportEmail;
    }
}
